<template>
  <div class="timer-counter">
    <v-layout justify-center
      id='counter'
      :class="{ started: isStarted }">
      <v-alert class='timer-alert' :value='alertValue !== ""' outlined type='success' text transition='fade-transition'>{{alertValue}}</v-alert>
      <v-flex xs11>
        <v-card v-if='typingTimer'>
          <textarea v-model='timerStr' rows='1' id='counterInput' @keydown.enter='inputTime' placeholder='Enter Time' ></textarea>
        </v-card>
        <div v-else>
          <v-card v-if='isStarted' flat class='transition-fast-in-fast-out' color='#efffe7f0'>
            <v-card-text ref='counter'>
              {{ displayCounter }}
            </v-card-text>
          </v-card>
          <v-card v-else flat>
            <v-card-text id='counterText' class='transition-fast-in-fast-out' ref='counter'>
              {{ displayCounter }}
            </v-card-text>
          </v-card>
        </div>
      </v-flex>
    </v-layout>
    <v-btn text :disabled='!$store.state.scrambleId' @click="solveMetadata('ok')">OK</v-btn>
    <v-btn text :disabled='!$store.state.scrambleId' @click="solveMetadata('+2')">+2</v-btn>
    <v-btn text :disabled='!$store.state.scrambleId' @click="solveMetadata('dnf')">DNF</v-btn>
  </div>
</template>

<script>
import Stackmat from 'stackmat'

export default {
  name: 'timer-counter',
  data () {
    return {
      // style data
      isStarted: false,

      // typing timer data
      typingTimer: false,
      timerStr: '',

      // timing data
      counter: 1500,
      realTimeStart: 0, // TODO: use realTimeStart for keeping track of actual nanoseconds between renders and set counter based on realtime data to counteract potential lag

      // logic data
      timer: null,
      counterState: 0, // TODO: maybe make this a string and use watch on it
      keypressed: 'up', // to prevent repeated firing of keypresses

      // stackmat.js
      stackmat: null,
      alertValue: ''
    }
  },
  props: {
    timerEnabled: Boolean
  },
  methods: {
    processTouchEvent (event) {
      // fake a key event if event type is touchstart/touchend in order to simplify event processing logic
      let fake = {}
      fake.preventDefault = () => { return event.preventDefault() }
      if (event.type === 'touchend') {
        fake.type = 'keyup'
        fake.key = ' '
        this.timerKeyUp(fake)
      } else if (event.type === 'touchstart') {
        fake.type = 'keydown'
        fake.key = ' '
        this.timerKeyDown(fake)
      }
    },
    timerKeyDown (event) {
      let enableStackmatFlag = false
      // TODO: don't show this when stackmat isn't plugged in
      // TODO: fix this so that it shows stackmat instructions on refresh (like cstimer)
      if (this.stackmat && this.stackmat.audioProcessor.context.state !== 'running') {
        this.stackmat.audioContext.resume().then(() => {
          event.preventDefault()
          this.alertValue = 'Stackmat enabled'
          setTimeout(() => { this.alertValue = '' }, 2500)
          enableStackmatFlag = true
        })
      }

      if (!enableStackmatFlag && this.timerEnabled && event) {
        if (event.key === ' ' || this.counterState === 2 || this.keypressed === 'down' || this.keypressed === 'debouncing') event.preventDefault()
        if (this.keypressed === 'up') {
          if (event.key.length === 1) this.setKeyPressed()
          this.processEvent(event)
        }
      }
    },
    timerKeyUp (event) {
      if (this.timerEnabled && this.keypressed === 'down') {
        this.setKeyUnpressed()
        this.processEvent(event)
      }
    },
    processEvent (event) {
      if (this.counterState === 0 && event.key === ' ') {
        if (!this.$store.state.sessionTimesSettings.inspectionEnabled) {
          this.isStarted = true
          this.counterState = 1
        } else if (event.type === 'keydown') {
          // initial countdown starting
          this.isStarted = true
          this.$refs.counter.style.color = 'green'
          this.$emit('begin-timer') // notify SessionBase that timer window has begun and any new scrambles should be defered until stop timer
        } else if (event.type === 'keyup') {
          // start countdown
          this.$refs.counter.style.color = 'red'
          this.startCountdown()
        }
      }
      if (this.counterState === 1 && event.key === ' ') {
        if (event.type === 'keydown') {
          // indicate timer starting
          this.$refs.counter.style.color = 'green'
        } else if (event.type === 'keyup') {
          // start timer
          this.$refs.counter.style.color = 'black'
          this.startTimer()
        }
      }
      if (this.counterState === 2) {
        if (event.type === 'keydown') {
          this.stopTimer()
          this.$refs.counter.style.color = 'red' // finished solve and keydown
        } else if (event.type === 'keyup') {
          this.$refs.counter.style.color = 'black'
          this.isStarted = false
        }
      }

      if (event.type === 'keyup' && (event.key === ' ' || this.counterState === 2)) this.counterState = (this.counterState + 1) % 3
      if (event.type === 'keydown' && event.key === 'Escape') this.resetTimer()
    },
    solveMetadata (metadata) {
      this.$emit('stop-timer', metadata, 'update')
    },
    stopTimer () {
      clearInterval(this.timer)
      this.$store.commit('setTime', this.counter / 100)
      this.$emit('stop-timer', 'ok')
    },
    startTimer () {
      clearInterval(this.timer)
      this.counter = 0
      this.realTimeStart = window.performance.now()
      this.timer = setInterval(() => {
        this.counter = (window.performance.now() - this.realTimeStart) / 10 // performance.now() is in milliseconds
      }, 10)
    },
    resetTimer () {
      clearInterval(this.timer)
      this.$emit('stop-timer')
      this.counterState = 0
      this.isStarted = false
      this.$refs.counter.style.color = 'black'
    },
    startCountdown () {
      this.counter = 1500
      this.timer = setInterval(() => {
        if (this.counter > 0) {
          this.counter -= 100
        } else {
          clearInterval(this.timer)
          this.counter = 1500
          this.counterState = 0
          this.isStarted = false
          this.$refs.counter.style.color = 'black' // finished solve
        }
      }, 1000)
    },
    // Set keypressed and keyunpressed for debouncing
    setKeyPressed () {
      this.keypressed = 'down'
    },
    setKeyUnpressed () {
      this.keypressed = 'debouncing'
      setTimeout(() => {
        this.keypressed = 'up'
      }, 200)
    },
    inputTime (e) {
      e.preventDefault()
      if (this.timerStr.toLowerCase().startsWith('d')) {
        this.$store.commit('setTime', 10)
        this.$emit('stop-timer', 'dnf')
        this.timerStr = ''
        return
      }
      const input = this.timerStr.split('').reverse().join('') // match on the reversed string so we match the milliseconds first.
      const matches = input.match(/^(\d{0,2})\.?(\d{0,2}):?(\d{0,2}):?(\d*)$/)
      if (matches === null) return // timer input doesn't match so exit without storing time (eg. input contains letters)
      const units = matches.slice(1).map(v => (v ? parseInt(v.split('').reverse().join(''), 10) : 0))

      let time = units[0] + 100 * units[1] + 6000 * units[2] + 360000 * units[3]
      this.$store.commit('setTime', time / 100)
      this.$emit('stop-timer', 'ok')
      this.timerStr = ''
    }
  },
  computed: {
    displayCounter () {
      if (this.counterState === 1) {
        return Math.round(this.counter / 100)
      } else {
        if (this.counter < 6000) {
          return (this.counter / 100).toFixed(2)
        } else if (this.counter < 360000) {
          return (Math.floor(this.counter / 6000) % 60) + ':' + ((Math.floor(this.counter / 100) % 60) + '.').padStart(3, '0') + ((this.counter % 100) + '').padStart(2, '0')
        } else {
          return Math.floor(this.counter / 360000) + ':' + ((Math.floor(this.counter / 6000) % 60) + ':').padStart(3, '0') + ((Math.floor(this.counter / 100) % 60) + '.').padStart(3, '0') + ((this.counter % 100) + '').padStart(2, '0')
        }
      }
    }
  },
  mounted () {
    this.typingTimer = this.$store.state.sessionTimesSettings.typingTimer
    if (!this.typingTimer) {
      window.addEventListener('keydown', this.timerKeyDown)
      window.addEventListener('keyup', this.timerKeyUp)
      document.getElementById('counter').addEventListener('touchstart', this.processTouchEvent)
      document.getElementById('counter').addEventListener('touchend', this.processTouchEvent)
    }

    if (this.$store.state.sessionTimesSettings.stackmatEnabled) {
      var self = this
      this.stackmat = new Stackmat()
      this.stackmat.on('started', () => {
        self.$refs.counter.style.color = 'black'
        self.isStarted = true
        self.counterState = 2
        self.startTimer()
      })

      const stopTimer = (packet) => {
        self.isStarted = false
        self.counterState = 0
        self.counter = packet.timeInMilliseconds / 10
        self.stopTimer()
      }

      var oldStatus = 'I'
      this.stackmat.on('packetReceived', (packet) => {
        if (packet.status === ' ') {
          self.counter = packet.timeInMilliseconds / 10
        } else if (packet.status === 'I' && oldStatus === ' ') {
          stopTimer(packet)
        }
        oldStatus = packet.status
      })

      this.stackmat.on('stopped', (packet) => {
        stopTimer(packet)
      })

      this.stackmat.start()
    }
  },
  destroyed () {
    window.removeEventListener('keydown', this.timerKeyDown)
    window.removeEventListener('keyup', this.timerKeyUp)
  }
}
</script>

<style scoped>
#counter.started {
  background-color: #efffe7f0;
  font-size: 15vh;
  position: fixed;
  padding-top: 35vh;
  top: 0; left: 0; bottom: 0; right: 0;
  width: 100vw; height: 100%;
  z-index: 20;
}
.v-card__text {
  font-size: unset;
  color: black;
}

.timer-alert {
  position: fixed;
  width: 50vw;
  top: 40px;
}

#counterInput {
  display: inline-block;
  text-align: center;
  width: 80%;
  font-size: 10vh;
  background: #efffe7f0;
  resize: none;
}

@media only screen and (max-width: 1080px) {
  #counterText {
    font-size: 10vh !important;
  }
}
</style>
